import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import { Plus, Trash2, Edit, AlertCircle, Check } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../../services/api';

const MissingReceiptLedger = ({ 
  reportId, 
  mrlItems, 
  setMrlItems, 
  mrlSignature, 
  setMrlSignature, 
  readOnly = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    merchant_info: '',
    transaction_date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    reason: ''
  });
  const [editItem, setEditItem] = useState({
    id: null,
    merchant_info: '',
    transaction_date: '',
    description: '',
    reason: ''
  });
  const [signature, setSignature] = useState('');

  const handleAddItem = async () => {
    if (!newItem.merchant_info || !newItem.transaction_date || !newItem.description || !newItem.reason) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const createdItem = await apiService.addMRLItem(reportId, newItem);
      
      setMrlItems([...mrlItems, createdItem]);
      setAddItemDialogOpen(false);
      setNewItem({
        merchant_info: '',
        transaction_date: format(new Date(), 'yyyy-MM-dd'),
        description: '',
        reason: ''
      });
      setSuccess('Item added successfully');
      
      setLoading(false);
    } catch (err) {
      console.error('Error adding MRL item:', err);
      setError(`Error adding item: ${err.message}`);
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    // Handle date without timezone conversion
    let dateValue = '';
    if (item.transaction_date) {
      // If date is already in YYYY-MM-DD format, use it directly
      if (typeof item.transaction_date === 'string' && item.transaction_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateValue = item.transaction_date;
      } else {
        // Parse date carefully to avoid timezone issues
        const dateStr = item.transaction_date.toString();
        if (dateStr.includes('T')) {
          // If it's an ISO string, take just the date part
          dateValue = dateStr.split('T')[0];
        } else {
          dateValue = dateStr;
        }
      }
    }
    
    setEditItem({
      id: item.id,
      merchant_info: item.merchant_info,
      transaction_date: dateValue,
      description: item.description,
      reason: item.reason
    });
    setEditDialogOpen(true);
  };

  const handleUpdateItem = async () => {
    if (!editItem.merchant_info || !editItem.transaction_date || !editItem.description || !editItem.reason) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const updatedItem = await apiService.updateMRLItem(reportId, editItem.id, editItem);
      
      setMrlItems(mrlItems.map(item => 
        item.id === editItem.id ? updatedItem : item
      ));
      setEditDialogOpen(false);
      setSuccess('Item updated successfully');
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating MRL item:', err);
      setError(`Error updating item: ${err.message}`);
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteMRLItem(reportId, itemId);
      
      setMrlItems(mrlItems.filter(item => item.id !== itemId));
      setSuccess('Item deleted successfully');
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting MRL item:', err);
      setError(`Error deleting item: ${err.message}`);
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signature.trim()) {
      setError('Signature is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.signMRL(reportId, { signature });
      
      setMrlSignature(signature);
      setSignDialogOpen(false);
      setSuccess('MRL signed successfully');
      
      setLoading(false);
    } catch (err) {
      console.error('Error signing MRL:', err);
      setError(`Error signing MRL: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Missing Receipt Ledger</Typography>
        <Typography variant="body2" color="text.secondary">
          Use this form to document any expenses that appear on your bank statement but you do not have a receipt.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Plus />}
            onClick={() => setAddItemDialogOpen(true)}
            disabled={loading}
          >
            Add Missing Receipt
          </Button>
        </Box>
      )}

      {mrlItems.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Merchant</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Reason</TableCell>
                {!readOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {mrlItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{(() => {
                    // Format date without timezone conversion
                    if (!item.transaction_date) return '';
                    
                    let dateStr = item.transaction_date.toString();
                    if (dateStr.includes('T')) {
                      dateStr = dateStr.split('T')[0];
                    }
                    
                    // Convert YYYY-MM-DD to MM/DD/YYYY
                    const parts = dateStr.split('-');
                    if (parts.length === 3) {
                      const [year, month, day] = parts;
                      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
                    }
                    return dateStr;
                  })()}</TableCell>
                  <TableCell>{item.merchant_info}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.reason}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEditItem(item)}
                          disabled={loading}
                        >
                          <Edit size={18} />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteItem(item.id)}
                          disabled={loading}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <AlertCircle size={24} style={{ marginBottom: 8, color: '#666' }} />
          <Typography variant="body1" color="text.secondary">
            No missing receipts documented
          </Typography>
        </Paper>
      )}

      <Box sx={{ mb: 2 }}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Employee Certification
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By signing below, I certify that the expenses listed above were incurred for company business purposes 
          and no receipt was obtained or the receipt was lost. I understand that consistent failure to provide 
          receipts may result in disciplinary action.
        </Typography>

        {mrlSignature ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Check color="green" size={18} style={{ marginRight: 8 }} />
              <Typography variant="subtitle1">
                Signed: {mrlSignature}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {format(new Date(), 'MM/dd/yyyy')}
              </Typography>
            </Box>
          </Paper>
        ) : mrlItems.length > 0 && !readOnly ? (
          <Button
            variant="contained"
            onClick={() => setSignDialogOpen(true)}
            disabled={loading}
          >
            Sign MRL
          </Button>
        ) : null}
      </Box>

      {/* Add Item Dialog */}
      <Dialog
        open={addItemDialogOpen}
        onClose={() => !loading && setAddItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Missing Receipt</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant Information"
                value={newItem.merchant_info}
                onChange={(e) => setNewItem({ ...newItem, merchant_info: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Transaction Date"
                value={newItem.transaction_date}
                onChange={(e) => setNewItem({ ...newItem, transaction_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description of Purchase"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Missing Receipt"
                value={newItem.reason}
                onChange={(e) => setNewItem({ ...newItem, reason: e.target.value })}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddItemDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Adding...' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !loading && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Missing Receipt</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant Information"
                value={editItem.merchant_info}
                onChange={(e) => setEditItem({ ...editItem, merchant_info: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Transaction Date"
                value={editItem.transaction_date}
                onChange={(e) => setEditItem({ ...editItem, transaction_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description of Purchase"
                value={editItem.description}
                onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Missing Receipt"
                value={editItem.reason}
                onChange={(e) => setEditItem({ ...editItem, reason: e.target.value })}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateItem}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Updating...' : 'Update Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sign MRL Dialog */}
      <Dialog
        open={signDialogOpen}
        onClose={() => !loading && setSignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sign Missing Receipt Ledger</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            By signing below, I certify that the expenses listed were incurred for company business purposes 
            and no receipt was obtained or the receipt was lost. I understand that consistent failure to provide 
            receipts may result in disciplinary action.
          </Typography>
          <TextField
            fullWidth
            label="Electronic Signature"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Type your full name as your electronic signature"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSignDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSign}
            variant="contained"
            disabled={loading || !signature.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Signing...' : 'Sign MRL'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MissingReceiptLedger;